import { Component, inject } from '@angular/core';
import { Location } from '@angular/common';
import { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { of, pipe, switchMap } from 'rxjs';
import { ReservationsService } from './reservations.service';
import {
  ReservationsUpsertDialogComponent,
  ReservationUpsertDialogConfig
} from './reservations-upsert-dialog/reservations-upsert-dialog.component';
import { GridDataRequest } from '@app/shared/components/api-paginated-grid/api-paginated-grid.model';
import { ApiPaginatedGridComponent } from '@app/shared/components/api-paginated-grid/api-paginated-grid.component';
import { ReservationFilterComponent } from './reservation-filter/reservation-filter.component';
import { ReservationFilter } from './reservations.model';
import { ActivatedRoute, Router } from '@angular/router';
import { ReservationStatusRendererComponent } from '@app/shared/components/simple-grid/reservation-status-renderer/reservation-status-renderer.component';

@Component({
  selector: 'app-reservations',
  standalone: true,
  imports: [ApiPaginatedGridComponent, ReservationFilterComponent],
  templateUrl: './reservations.component.html',
  host: { class: 'd-flex flex-fill' }
})
export class ReservationsComponent {
  private _modalService = inject(NgbModal);
  private _reservationsService = inject(ReservationsService);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _location = inject(Location);
  private _filter: ReservationFilter = {};
  initialFilterState: ReservationFilter = {};

  listReservationsOperator = pipe(
    switchMap((dataRequest: GridDataRequest) =>
      this._reservationsService.getReservations({
        pageInfo: dataRequest.pageInfo,
        filter: this._filter
      })
    )
  );

  createReservationOperator = pipe(
    switchMap(() => {
      const options: NgbModalOptions = {
        fullscreen: 'xxl',
        scrollable: true,
        size: 'xl',
        centered: true,
        modalDialogClass: 'align-items-stretch'
      };
      const modalRef = this._modalService.open(ReservationsUpsertDialogComponent, options);
      modalRef.componentInstance.config.set({ mode: 'create' });
      return modalRef.closed;
    })
  );

  updateReservationOperator = pipe(
    switchMap((uid: string) => {
      this._router.navigate([], {
        queryParams: { selected: uid },
        queryParamsHandling: 'merge'
      });
      return of(void 0);
    })
  );

  columnDefs: ColDef[] = [
    {
      field: 'number',
      minWidth: 80,
      maxWidth: 80,
      pinned: 'left'
    },
    {
      field: 'rentalAgreementNumber',
      headerName: 'RA Number',
      minWidth: 80,
      maxWidth: 80
    },
    {
      field: 'referenceNumber',
      headerName: 'Ref. Number',
      minWidth: 100
    },
    {
      field: 'status',
      cellRenderer: ReservationStatusRendererComponent,
      minWidth: 110
    },
    {
      field: 'driverFirstName',
      minWidth: 120
    },
    {
      field: 'driverLastName',
      minWidth: 120
    },
    {
      field: 'locationOut',
      minWidth: 100
    },
    {
      field: 'locationIn',
      minWidth: 100
    },
    {
      field: 'dateOut',
      valueFormatter: (params: ValueFormatterParams) => new Date(params.value).toLocaleString(),
      minWidth: 90,
      tooltipValueGetter: (params) => new Date(params.value).toLocaleString()
    },
    {
      field: 'dateIn',
      valueFormatter: (params: ValueFormatterParams) => new Date(params.value).toLocaleString(),
      minWidth: 90,
      tooltipValueGetter: (params) => new Date(params.value).toLocaleString()
    },
    {
      field: 'clientNumber',
      headerName: 'Broker Nr.',
      minWidth: 80
    },
    {
      field: 'reservationCreatedDate',
      valueFormatter: (params: ValueFormatterParams) => new Date(params.value).toLocaleString(),
      minWidth: 90
    }
  ];

  constructor() {
    this._route.queryParams.subscribe((x) => {
      const { selected, selectedByNumber, selectedVersion, ...filter } = x;

      this._filter = filter;
      this.initialFilterState = filter;
      this._modalService.dismissAll('quite');

      // Note: don't gate on hasOpenModals() here — dismissAll() clears the modal registry on a
      // microtask, so it would still report true right after dismissing and block reopening.
      // That breaks "View this version", which navigates while the dialog is already open.
      if (selected || selectedByNumber) {
        const options: NgbModalOptions = {
          fullscreen: 'xxl',
          scrollable: true,
          size: 'xl',
          centered: true,
          modalDialogClass: 'align-items-stretch'
        };
        const modalRef = this._modalService.open(ReservationsUpsertDialogComponent, options);

        const config: ReservationUpsertDialogConfig =
          selectedVersion && !isNaN(Number(selectedVersion))
            ? { mode: 'readonly' as const, uid: selected, version: Number(selectedVersion) }
            : { mode: 'readonly' as const, uid: selected, number: selectedByNumber };

        modalRef.componentInstance.config.set(config);

        modalRef.dismissed
          .pipe(
            switchMap((x) => {
              if (x === 'quite') {
                return of(void 0);
              }
              this._location.back();
              return of(void 0);
            })
          )
          .subscribe();
      }
    });
  }

  filterChanged(newFilter: ReservationFilter): void {
    this._router.navigate(['/operations/reservations'], { queryParams: newFilter });
  }
}
